import {
  cleanCurrency,
  isValidCurrency
} from "@toorak/tc-common-util-sdk/dist/errorCountService/fieldMissingCount";
import {
  thirdPartyValuationStatus,
  yesNoDropdown,
  closingCostValues
} from "../../../create-loan/constants/loanCreationDropDownValues";
import { sanitizeCurrency } from "../../../utils/formatChecks";
import { LoanSizerEnum } from "../../../frontend-sizer/FrontendSizerTemplate";
import { LoanStatusEnum } from "@toorak/tc-common-util-sdk";
import { LoanStage } from "@toorak/tc-common-fe-sdk";

const getIsRequiredForAppraisalArvValue = (currentData: any) => {
  const { result, appraisalAsIsValue, index } = currentData || {};
  const { loanDetails, editedValues, loanResult, loanCharacterisations } =
    result || {};
  const loanId = Object.keys(loanResult || {})[0];
  const isMultiPropertyLoan = loanDetails?.propertyDetails?.length > 1;

  let returnValue = true;

  let loanCharacterisation: string = loanResult?.[loanId]?.loanCharacterisations?.stabilizedFlag;

  const isLoanRefinance =
    loanDetails?.fesLoanInformation?.loanPurpose === "Refinance";

  const isLoanPurposePurchase =
    ["Delayed Purchase", "Purchase"].includes(loanDetails?.fesLoanInformation?.loanPurpose);

  const isStabilizedLoan = loanCharacterisation === "Stabilized";
  if (loanDetails?.loanState !== LoanStatusEnum.DataEntry) {
    const tempTotalBudget =
      editedValues?.loanEconomics?.totalBudgetAmount ??
      loanDetails?.fesLoanEconomics?.rehabBudget;
    let tempAsIsValue =
      editedValues?.propertyDetails?.[loanDetails?.activePropertyId]
        ?.propertyEconomics?.originalAsIsAppraisalValue ??
      appraisalAsIsValue ??
      loanDetails?.propertyDetails?.[loanDetails?.activePropertyId - 1]
        ?.fesPropertyEconomics?.payload?.originalAsIsAppraisalValue;
    let tempPurchasePrice =
      editedValues?.propertyDetails?.[loanDetails?.activePropertyId]
        ?.propertyEconomics?.purchasePrice ??
      loanDetails?.propertyDetails?.[loanDetails?.activePropertyId - 1]
        ?.fesPropertyEconomics?.payload?.purchasePrice;

      if (isMultiPropertyLoan) {
        let tempAggregatedAsIsValue = 0;
        let tempAggregatedPurchasePrice = 0;

        loanDetails?.propertyDetails?.forEach((prop: any, propIndex: number) => {
          const purchasePriceValue =
            editedValues?.propertyDetails?.[propIndex]?.propertyEconomics
              ?.purchasePrice ||
            prop?.propertyEconomics?.payload?.purchasePrice ||
            "0";
          const asIsValue =
            editedValues?.propertyDetails?.[loanDetails?.activePropertyId]
              ?.propertyEconomics?.originalAsIsAppraisalValue ||
            prop?.valuations?.[index]?.payload?.appraisalAsIsValue ||
            "0";

          tempAggregatedPurchasePrice += parseFloat(purchasePriceValue?.replace(/[^0-9.-]/g, ""));
          tempAggregatedAsIsValue += parseFloat(asIsValue?.replace(/[^0-9.-]/g, ""));
        })
        tempAsIsValue = tempAggregatedAsIsValue.toString();
        tempPurchasePrice = tempAggregatedPurchasePrice.toString();
      }

         /* Final data for the checks */
    const totalBudget = parseFloat(
     tempTotalBudget ?  typeof tempTotalBudget === "string"
        ? Number(tempTotalBudget.replace(/[^0-9.]/g, ""))
        : tempTotalBudget || 0 : 0
    );
    const asIsValue = parseFloat(
      tempAsIsValue ?  typeof tempAsIsValue === "string"
        ? Number(tempAsIsValue.replace(/[^0-9.]/g, ""))
        : tempAsIsValue || 0 : 0
    );
    const purchasePrice = parseFloat(
      tempPurchasePrice ? typeof tempPurchasePrice === "string"
        ? Number(tempPurchasePrice.replace(/[^0-9.]/g, ""))
        : tempPurchasePrice || 0 : 0
    );
    if (isLoanPurposePurchase) {
      const minValue = Math.min(asIsValue, purchasePrice);
      returnValue = totalBudget <= 0.05 * minValue ? false : true;
    } else if (isLoanRefinance) {
      returnValue = totalBudget <= 0.05 * asIsValue ? false : true;
    } else if (isStabilizedLoan) {
      returnValue = false;
    }
  } else {
    returnValue = !isStabilizedLoan;
  }
  return returnValue;
};

const FesoriginalAsIsValue = {
  fieldName: "originalAsIsAppraisalValue",
  commentField: "originalAsIsAppraisalValue",
  backendKey: "originalAsIsAppraisalValue",
  eidtedLabel: "originalAsIsAppraisalValue",
  fieldLabel: "As-Is Value ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const FesoriginalAsIsAppraisalValue = {
  fieldName: "originalAsIsAppraisalValue",
  backendKey: "originalAsIsAppraisalValue",
  eidtedLabel: "originalAsIsAppraisalValue",
  fieldLabel: "As Is Valuation ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const FesThirdPartyValuationStatus = {
  fieldName: "thirdPartyValuationStatus",
  eidtedLabel: "thirdPartyValuationStatus",
  fieldLabel: "Third Party Valuation - Status",
  colSpan: 4,
  required: () => false,
  fieldType: () => "dropDown",
  dropDownData:
    thirdPartyValuationStatus.map((ele) => ({ label: ele, value: ele })) || [],
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const originalAsIsAppraisalValue = {
  fieldName: "originalAppraisalValue",
  backendKey: "originalAsIsAppraisalValue",
  eidtedLabel: "originalAsIsAppraisalValue",
  fieldLabel: 'Original "As Is" Appraisal Value ($)',
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable,
  commentField: "originalAsIsAppraisalValue"
};

const bridgeOriginalAsIsAppraisalValue = {
  fieldName: "originalAsIsAppraisalValue",
  eidtedLabel: "originalAsIsAppraisalValue",
  fieldLabel: 'Original "As Is" Appraisal Value ($)',
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable,
  commentField: "originalAsIsAppraisalValue"
};

const originalAsRepairedAppraisedValue = {
  fieldName: "originalAsRepairedAppraisedValue",
  eidtedLabel: "originalAsRepairedAppraisedValue",
  fieldLabel: "After-Repair Value ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const FesthirdPartyValuation = {
  fieldName: "thirdPartyValuation",
  backendKey: "thirdPartyValuation",
  eidtedLabel: "thirdPartyValuation",
  fieldLabel: "Third Party Valuation - Property Value ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const thirdPartyValuation = {
  fieldName: "thirdPartyValuation",
  eidtedLabel: "thirdPartyValuation",
  fieldLabel: "Third Party Valuation ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => false,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const FesPurchasePrice = {
  fieldName: "purchasePrice",
  backendKey: "purchasePrice",
  eidtedLabel: "purchasePrice",
  fieldLabel: "Property Acquisition Price ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const purchasePrice = {
  fieldName: "purchasePrice",
  backendKey: "purchasePrice",
  eidtedLabel: "purchasePrice",
  fieldLabel: "Purchase Price ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const bridgeFES14ClosingCost = {
  fieldName: "closingCost",
  fieldLabel: "Closing Costs (%)",
  eidtedLabel: "closingCost",
  colSpan: 4,
  required: () => true,
  fieldType: () => "dropDown",
  dropDownData: closingCostValues,
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable,
  ChangeValueMethod: (value: any) => parseFloat(value)
};

const costBasis = {
  fieldName: "costBasis",
  backendKey: "costBasis",
  eidtedLabel: "costBasisAfterCostIncurred",
  fieldLabel: "Cost Basis (After Cost Incurred) ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (
    isEvaluationPanelVisible: any,
    isEditable: any,
    loanState?: any,
    loanType?: any,
    loanStage?: any
  ) =>
    (loanStage === "FES" && loanType === "BridgeLoan") ||
    (isEvaluationPanelVisible && !isEditable)
};

const annualPropertyTaxes = {
  fieldName: "annualPropertyTaxes",
  backendKey: "annualPropertyTaxes",
  eidtedLabel: "annualPropertyTaxes",
  fieldLabel: "Annual Property Taxes ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: (data: any) => {
    if (data?.stabilizedFlag === "Stabilized") {
      return true;
    }
    if (
      data?.result?.loanDetails?.loanStage === LoanStage.fes &&
      data?.loanSizerType === LoanSizerEnum.multifamilyMixedUse
    ) {
      return true;
    }
    return false;
  },
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const annualPropertyTaxesDscr = {
  fieldName: "annualPropertyTaxes",
  backendKey: "annualPropertyTaxes",
  eidtedLabel: "annualPropertyTaxes",
  fieldLabel: "Annual Property Taxes ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: (data: any) => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const annualHazardInsurance = {
  fieldName: "annualHazardInsurance",
  backendKey: "annualHazardInsurance",
  eidtedLabel: "annualHazardInsurance",
  fieldLabel: "Annual Hazard Insurance ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const annualFloodInsurance = {
  fieldName: "annualFloodInsurance",
  backendKey: "annualFloodInsurance",
  eidtedLabel: "annualFloodInsurance",
  fieldLabel: "Annual Flood Insurance ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const annualHoaFee = {
  fieldName: "annualHOAFee",
  backendKey: "annualHoaFee",
  eidtedLabel: "annualHoaFee",
  fieldLabel: "Annual HOA Fee ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const dscrFESAnnualHoaFee = {
  fieldName: "annualHOAFee",
  backendKey: "annualHoaFee",
  eidtedLabel: "annualHoaFee",
  fieldLabel: "Annual HOA Fee ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const bridgeFESAnnualHoaFee = {
  fieldName: "annualHOAFee",
  backendKey: "annualHoaFee",
  eidtedLabel: "annualHoaFee",
  fieldLabel: "Annual HOA Fee ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const decliningMarkets = {
  fieldName: "decliningMarkets",
  backendKey: "decliningMarkets",
  eidtedLabel: "decliningMarkets",
  fieldLabel: "Declining Market",
  colSpan: 4,
  required: () => false,
  fieldType: () => "dropDown",
  dropDownData: yesNoDropdown.map((ele) => ({ label: ele, value: ele })) || [],
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const dscrFesDecliningMarkets = {
  fieldName: "decliningMarkets",
  backendKey: "decliningMarkets",
  eidtedLabel: "decliningMarkets",
  fieldLabel: "Declining Market",
  colSpan: 4,
  required: () => false,
  fieldType: () => "dropDown",
  dropDownData: yesNoDropdown.map((ele) => ({ label: ele, value: ele })) || [],
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const originalRepairedAppraisalValue = {
  fieldName: "originalRepairedAppraisalValue",
  eidtedLabel: "originalRepairedAppraisalValueEdited",
  fieldLabel: "As Repaired Value (ARV) ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable,
  commentField: "originalAsRepairedAppraisedValue"
};

const bridgeOriginalRepairedAppraisalValue = {
  fieldName: "originalAsRepairedAppraisedValue",
  eidtedLabel: "originalRepairedAppraisalValueEdited",
  fieldLabel: "As Repaired Value (ARV) ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: (currentData: any) => getIsRequiredForAppraisalArvValue(currentData),
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable,
  commentField: "originalAsRepairedAppraisedValue"
};

const grossPotentialRent = {
  fieldName: "grossPotentialRent",
  backendKey: "grossPotentialRent",
  eidtedLabel: "grossPotentialRent",
  fieldLabel: "Gross Potential Rent (Yearly) ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: (data: any) => {
    if (data?.stabilizedFlag === "Stabilized") {
      return true;
    }
    if (
      data?.result?.loanDetails?.loanStage === LoanStage.fes &&
      data?.loanSizerType !== LoanSizerEnum.groundUp
    ) {
      return true;
    }
    return false;
  },
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const grossCondoSelloutValue = {
  fieldName: "grossCondoSelloutValue", // new field
  fieldLabel: "Gross Condo Sellout Value ($)",
  fieldType: () => "currency",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => false,
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const assignmentFees = {
  fieldName: "assignmentFees",
  backendKey: "assignmentFees",
  eidtedLabel: "assignmentFeesEdited",
  fieldLabel: "Assignment Fees ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const insurance = {
  fieldName: "insurance",
  backendKey: "insurance",
  eidtedLabel: "insurance",
  fieldLabel: "Insurance ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: (data: any) => {
    if (data?.stabilizedFlag === "Stabilized") {
      return true;
    }
    if (
      data?.result?.loanDetails?.loanStage === LoanStage.fes &&
      data?.loanSizerType === LoanSizerEnum.multifamilyMixedUse
    ) {
      return true;
    }
    return false;
  },
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const appraisalReported = {
  fieldName: "appraisalReported",
  backendKey: "appraisalReportedNoiNcf",
  eidtedLabel: "appraisalReported",
  fieldLabel: "Appraisal Reported NOI/NCF ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable,
  commentField: "appraisalReportedNoiNcf"
};

const salesPrice = {
  fieldName: "salesPrice",
  backendKey: "salesPrice",
  eidtedLabel: "salesPrice",
  fieldLabel: "Sales Price ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => false,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: () => true,
  commentField: "salesPrice"
};

const bridgeFesMMAppraisalReported = {
  fieldName: "appraisalReportedNoiNcf",
  backendKey: "appraisalReportedNoiNcf",
  eidtedLabel: "appraisalReported",
  fieldLabel: "Appraisal Reported NOI/NCF ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable,
  commentField: "appraisalReportedNoiNcf"
};

const borrowerPaidCosts = {
  fieldName: "borrowerPaidCosts",
  backendKey: "borrowerPaidCosts",
  eidtedLabel: "borrowerPaidCosts",
  fieldLabel: "Borrower Paid costs to date",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const marketRentPerMonth = {
  fieldName: "marketRentPerMonth",
  fieldLabel: "Market Rent per month (if applicable) ($)",
  errorChecks: isValidCurrency,
  ChangeValueMethod: cleanCurrency,
  FocusOutMethod: sanitizeCurrency,
  colSpan: 4,
  required: () => true,
  fieldType: () => "currency",
  isVisible: () => true,
  isViewMode: (isEvaluationPanelVisible: any, isEditable: any) =>
    isEvaluationPanelVisible && !isEditable
};

const dscrFESFields = [
  FesoriginalAsIsAppraisalValue,
  FesThirdPartyValuationStatus,
  FesthirdPartyValuation,
  FesPurchasePrice,
  annualPropertyTaxesDscr,
  annualHazardInsurance,
  annualFloodInsurance,
  dscrFESAnnualHoaFee,
  dscrFesDecliningMarkets
];

const bridgeFesOneToFourFields: any = [
  bridgeOriginalAsIsAppraisalValue,
  purchasePrice,
  bridgeFES14ClosingCost,
  bridgeOriginalRepairedAppraisalValue,
  grossPotentialRent,
  { ...costBasis, required: () => false },
  assignmentFees,
  // TODO: below fields required for ta-6630 not required for refactoring prod release
  annualPropertyTaxes,
  insurance,
  bridgeFESAnnualHoaFee
];

const bridgeFesMultiFamilyFields: any = [
  bridgeOriginalAsIsAppraisalValue,
  purchasePrice,
  borrowerPaidCosts,
  bridgeOriginalRepairedAppraisalValue,
  grossPotentialRent,
  bridgeFesMMAppraisalReported,
  annualPropertyTaxes,
  { ...insurance, required: () => true },
  { ...costBasis, required: () => false },
  grossCondoSelloutValue,
  assignmentFees,
  annualHoaFee
];

const bridgeFesGroundFields: any = [
  FesoriginalAsIsValue,
  purchasePrice,
  grossPotentialRent,
  bridgeFES14ClosingCost,
  originalAsRepairedAppraisedValue,
  marketRentPerMonth,
  assignmentFees
];

const dscrPrePostFields = [
  { ...originalAsIsAppraisalValue, backendKey: "originalAsIsAppraisalValue" },
  thirdPartyValuation,
  purchasePrice,
  costBasis,
  annualPropertyTaxesDscr,
  annualHazardInsurance,
  annualFloodInsurance,
  { ...annualHoaFee, fieldName: "annualHoaFee" },
  decliningMarkets
];

const dscrPreFields: any = [
  { ...originalAsIsAppraisalValue, commentField: "originalAsIsAppraisalValue" },
  { ...thirdPartyValuation, required: () => false },
  purchasePrice,
  costBasis,
  annualPropertyTaxesDscr,
  annualHazardInsurance,
  annualFloodInsurance,
  { ...annualHoaFee, fieldName: "annualHoaFee" },
  { ...decliningMarkets, required: () => false }
];

const bridgePrePostFields: any = [
  // { ...originalAsIsAppraisalValue, backendKey: "originalAsIsAppraisalValue" },
  // {
  //   ...originalRepairedAppraisalValue,
  //   backendKey: "originalAsRepairedAppraisedValue"
  // },
  purchasePrice,
  // salesPrice,
  costBasis,
  grossPotentialRent,
  assignmentFees,
  annualPropertyTaxes,
  insurance,
  appraisalReported
];

const bridgePreFields: any = [
  // { ...originalAsIsAppraisalValue, backendKey: "originalAsIsAppraisalValue" },
  // {
  //   ...originalRepairedAppraisalValue,
  //   backendKey: "originalAsRepairedAppraisedValue"
  // },
  purchasePrice,
  // salesPrice,
  { ...costBasis, required: () => false },
  grossPotentialRent,
  assignmentFees,
  annualPropertyTaxes,
  insurance,
  appraisalReported
];

const getFeildsBasedOnLoanSizerType = (loanSizerType: any) => {
  switch (loanSizerType) {
    case LoanSizerEnum.multifamilyMixedUse:
      return bridgeFesMultiFamilyFields;
    case LoanSizerEnum.groundUp:
      return bridgeFesGroundFields;
    case LoanSizerEnum.oneFourFamily:
      return bridgeFesOneToFourFields;
    default:
      return [];
  }
};

export const getPropertyEconomicsField = (
  loanStage: any,
  loanType: any,
  loanSizerType: any
) => {
  switch (loanStage) {
    case "fes":
      return loanType === "InvestorDSCR"
        ? dscrFESFields
        : getFeildsBasedOnLoanSizerType(loanSizerType);
    case "pre":
      return loanType === "InvestorDSCR" ? dscrPreFields : bridgePreFields;
    case "post":
      return loanType === "InvestorDSCR"
        ? dscrPrePostFields
        : bridgePrePostFields;
    default:
      return [];
  }
};
